import _ from 'lodash';
import { Toast } from 'primereact/toast';
import { useEffect, useRef, useState } from 'react';
import {
  KeyValEntry,
  Phase,
  putUserEpConnectKeyValForAlpha,
  readAllPhaseUserKeyValuesByAlpha,
  readAllPhases,
  updatePhase
} from '../../../Helper/ApiHelper/KeyValueNetworkHelper';
import {
  AuthUserType,
  readAllServiceProvider
} from '../../../Helper/ApiHelper/LoginNetworkHelper';
import {
  EpTreeNode,
  generateEpTreeFromPositions,
  readEpCatalogForPhase
} from '../../../Helper/ApiHelper/LvEpNetworkHelper';
import {
  DURATION_NOTIFICATION_ERROR_LONG,
  SERVER_RESPONSE_ERROR,
  SERVER_RESPONSE_PENDING,
  SERVER_RESPONSE_SUCCESS
} from '../../../Helper/Statics/Constants';
import BeeLoadingSpinner from '../../Atoms/BeeLoadingSpinner';
import { FaktorKeyValueStore } from '../../Molecules/DL/DlBundeslandfaktoren';
import './AlphaSetupTabPhaseToUser.scss';
import NoPhases from '../../Molecules/Alpha/NoPhases';
import AlphaPhaseParticipationTable from '../../Molecules/Alpha/AlphaPhaseParticipationTable';
import AlphaPhaseEpCatalogViewDialog, {
  AlphaPhaseEpCatalogKeyValEntry
} from '../../Molecules/Dialogs/AlphaPhaseEpCatalogViewDialog';

export default function AlphaSetupTabPhaseToUser() {
  const [forceReload, setForceReload] = useState<boolean>(true);
  const [dataIsLoading, setDataIsLoading] = useState<boolean>(true);
  const [phasen, setPhasen] = useState<Phase[]>([]);
  const [serviceProvider, setServiceProvider] = useState<AuthUserType[]>([]);
  const [keyValueStore, setKeyValueStore] = useState<{
    [key: string]: FaktorKeyValueStore;
  }>({});
  const [phaseUserKeyValues, setPhaseUserKeyValues] = useState<{
    [key: string]: KeyValEntry[];
  }>({});
  const [epCatalogViewVisible, setEpCatalogViewVisible] =
    useState<boolean>(false);
  const [epCatalogViewPhase, setEpCatalogViewPhase] = useState<
    Phase | undefined
  >();
  const [epCatalogTree, setEpCatalogTree] = useState<EpTreeNode[] | null>(null);
  const [epCatalogLoading, setEpCatalogLoading] = useState<boolean>(false);
  const [epCatalogSaving, setEpCatalogSaving] = useState<boolean>(false);
  const [epCatalogReadOnly, setEpCatalogReadOnly] = useState<boolean>(false);
  const [epCatalogViewUser, setEpCatalogViewUser] = useState<
    AuthUserType | undefined
  >();
  const toast = useRef<Toast>(null);
  const epCatalogByPhaseIdRef = useRef<Record<string, EpTreeNode[]>>({});

  ////////////////////
  /////// HOOKS //////
  ///////////////////

  useEffect(() => {
    setDataIsLoading(true);
    setPhaseUserKeyValues({});
    readAllPhases()
      .then((phases: any) => {
        readAllServiceProvider()
          .then((provider: any) => {
            setupPhaseUserKeyValueStore(phases, provider);
            loadPhaseUserKeyValues(phases, provider).finally(() => {
              setDataIsLoading(false);
            });
          })
          .catch(() => {
            setPhaseUserKeyValues({});
            if (toast && toast.current) {
              toast.current.show({
                severity: 'error',
                summary: 'Dienstleister konnten nicht geladen werden',
                detail:
                  'Leider konnten die Dienstleister nicht geladen werden. Bitte prüfen Sie Ihre Internetverbindung und versuchen Sie es erneut. Sollte das Problem bestehen bleiben wenden Sie sich bitte an den Kundenservice.',
                sticky: true,
                closable: true,
                life: DURATION_NOTIFICATION_ERROR_LONG
              });
            }
            setDataIsLoading(false);
          });
      })
      .catch(() => {
        setPhaseUserKeyValues({});
        if (toast && toast.current) {
          toast.current.show({
            severity: 'error',
            summary: 'Phasen konnten nicht geladen werden',
            detail:
              'Leider konnten die Phasen nicht geladen werden. Bitte prüfen Sie Ihre Internetverbindung und versuchen Sie es erneut. Sollte das Problem bestehen bleiben wenden Sie sich bitte an den Kundenservice.',
            sticky: true,
            closable: true,
            life: DURATION_NOTIFICATION_ERROR_LONG
          });
        }
        setDataIsLoading(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [forceReload]);

  ///////////////////////
  ///// SERVERCALLS /////
  ///////////////////////

  function loadPhaseUserKeyValues(
    phases: Phase[],
    providers: AuthUserType[]
  ): Promise<void> {
    if (!phases?.length || !providers?.length) {
      setPhaseUserKeyValues({});
      return Promise.resolve();
    }
    return readAllPhaseUserKeyValuesByAlpha()
      .then((responseMap) => {
        const cache: { [key: string]: KeyValEntry[] } = {};
        phases.forEach((phase) => {
          providers.forEach((provider) => {
            const cacheKey = buildPhaseUserCacheKey(phase.id, provider.id);
            cache[cacheKey] = responseMap?.[phase.id]?.[provider.id] ?? [];
          });
        });
        setPhaseUserKeyValues(cache);
      })
      .catch(() => {
        setPhaseUserKeyValues({});
      });
  }

  function updateUserToPhase(
    phase: Phase,
    userId: string,
    checked: boolean,
    previousState: boolean
  ) {
    const valueKey = buildPhaseUserCacheKey(phase.id, userId);
    const current = keyValueStore[valueKey];
    if (
      !current ||
      (current.value === checked && current.process !== SERVER_RESPONSE_ERROR)
    ) {
      return;
    }
    setKeyValueStore((previous) => ({
      ...previous,
      [valueKey]: {
        id: valueKey,
        value: checked,
        process: SERVER_RESPONSE_PENDING
      }
    }));

    const currentlyConnected: string[] = [];
    serviceProvider.forEach((sp) => {
      const key = buildPhaseUserCacheKey(phase.id, sp.id);
      const status = keyValueStore[key];
      if (status && status.value && sp.id !== userId) {
        currentlyConnected.push(sp.id);
      }
    });
    if (checked) {
      currentlyConnected.push(userId);
    }
    const updatedPhase = _.cloneDeep(phase);
    updatedPhase.userIds = currentlyConnected;
    updatePhase(updatedPhase.id, updatedPhase)
      .then(() => {
        setKeyValueStore((previous) => ({
          ...previous,
          [valueKey]: {
            id: valueKey,
            value: checked,
            process: SERVER_RESPONSE_SUCCESS
          }
        }));
      })
      .catch(() => {
        setKeyValueStore((previous) => ({
          ...previous,
          [valueKey]: {
            id: valueKey,
            value: previousState,
            process: SERVER_RESPONSE_ERROR
          }
        }));
      });
  }

  function openEpCatalogView(
    phase: Phase,
    user: AuthUserType,
    readOnly = false
  ) {
    setEpCatalogViewPhase(phase);
    setEpCatalogViewUser(user);
    setEpCatalogReadOnly(readOnly);
    setEpCatalogViewVisible(true);

    const cachedTree = epCatalogByPhaseIdRef.current[phase.id];
    if (cachedTree) {
      setEpCatalogTree(cachedTree);
      setEpCatalogLoading(false);
      return;
    }

    setEpCatalogLoading(true);
    setEpCatalogTree(null);
    readEpCatalogForPhase(phase.id)
      .then((ep: any) => {
        const tree = generateEpTreeFromPositions(ep?.data ?? []) ?? [];
        epCatalogByPhaseIdRef.current[phase.id] = tree;
        setEpCatalogTree(tree);
      })
      .catch(() => {
        toast.current?.show({
          severity: 'error',
          summary: 'EP-Katalog konnte nicht geladen werden',
          detail:
            'Der EP-Katalog für diese Phase konnte nicht geladen werden. Bitte prüfen Sie dass ein EP-Katalog für diese Phase angelegt wurde.',
          closable: true,
          sticky: false,
          life: DURATION_NOTIFICATION_ERROR_LONG
        });
        setEpCatalogTree([]);
      })
      .finally(() => {
        setEpCatalogLoading(false);
      });
  }

  function handleEpCatalogSave(keyValRows: AlphaPhaseEpCatalogKeyValEntry[]) {
    const phase = epCatalogViewPhase;
    const user = epCatalogViewUser;
    if (!phase || !user) {
      return;
    }
    if (keyValRows.length === 0) {
      closeEpCatalogView();
      return;
    }
    /** Alpha keyval routes use the service-provider id in the `orga` path segment. */
    const orgaId = user.id;
    setEpCatalogSaving(true);
    putUserEpConnectKeyValForAlpha(orgaId, phase.id, keyValRows)
      .then((entries) => {
        const resolvedEntries = (entries ?? []) as KeyValEntry[];
        const cacheKey = buildPhaseUserCacheKey(phase.id, user.id);
        setPhaseUserKeyValues((previous) => ({
          ...previous,
          [cacheKey]: [...previous[cacheKey], ...resolvedEntries]
        }));
        toast.current?.show({
          severity: 'success',
          summary: 'Gespeichert',
          detail: 'Die EP-Auswahl wurde übernommen.',
          closable: true,
          sticky: false,
          life: 4000
        });
        closeEpCatalogView();
      })
      .catch(() => {
        toast.current?.show({
          severity: 'error',
          summary: 'Speichern fehlgeschlagen',
          detail:
            'Die EP-Auswahl konnte nicht gespeichert werden. Bitte versuchen Sie es erneut.',
          closable: true,
          sticky: false,
          life: DURATION_NOTIFICATION_ERROR_LONG
        });
      })
      .finally(() => {
        setEpCatalogSaving(false);
      });
  }

  //////////////////////
  //////// HELPERS /////
  //////////////////////

  function setupPhaseUserKeyValueStore(
    phases: Phase[],
    provider: AuthUserType[]
  ) {
    epCatalogByPhaseIdRef.current = {};
    const prov: AuthUserType[] = [...provider];
    //interner key = phaseId_lsId!!s
    const providerIds: string[] = prov.map((entry) => entry.id);
    const cleanedPhases: Phase[] = _.cloneDeep(phases);
    const values: { [key: string]: FaktorKeyValueStore } = {};
    cleanedPhases.forEach((phase) => {
      const originalUserIds = phase.userIds ? [...phase.userIds] : [];
      const providersForPhase = phase.userIds
        ? _.intersection(phase.userIds, providerIds)
        : [];
      phase.userIds = providersForPhase;
      if (originalUserIds.length !== providersForPhase.length) {
        const updatedPhase = _.cloneDeep(phase);
        updatedPhase.userIds = providersForPhase;
        updatePhase(updatedPhase.id, updatedPhase)
          .then(() => {
            setForceReload(!forceReload);
          })
          .catch(() => {
            if (toast && toast.current) {
              toast.current.show({
                severity: 'error',
                summary: 'Phasen konnte nicht geupdated werden',
                detail:
                  'Leider enthält mindestens eine Phase einen gelöschten User. Das Automatische Löschen ist fehlgeschlagen. Bitte laden Sie die Seite erneut. Sollte das Problem bestehen bleiben wenden Sie sich bitte an den Kundenservice.',
                sticky: true,
                closable: true,
                life: DURATION_NOTIFICATION_ERROR_LONG
              });
            }
          });
      }
      providerIds.forEach((userId) => {
        const key = buildPhaseUserCacheKey(phase.id, userId);
        const isParticipating = providersForPhase.includes(userId);
        values[key] = {
          id: key,
          value: isParticipating,
          process: SERVER_RESPONSE_SUCCESS
        };
      });
    });
    setPhasen(_.sortBy(cleanedPhases, ['start', 'end', 'title']));
    setKeyValueStore(values);
    setServiceProvider(prov);
  }

  function closeEpCatalogView() {
    setEpCatalogReadOnly(false);
    setEpCatalogViewVisible(false);
    setEpCatalogViewPhase(undefined);
    setEpCatalogViewUser(undefined);
    setEpCatalogTree(null);
    setEpCatalogLoading(false);
    setEpCatalogSaving(false);
  }

  function buildPhaseUserCacheKey(phaseId: string, userId: string) {
    return `${phaseId}_${userId}`;
  }

  /////////////////////
  ///////// UI ////////
  /////////////////////
  function injectDescription() {
    return (
      <div className={'mb-3'}>
        Bitte geben Sie in folgender Tabelle an, welche Nutzer an welcher Phase
        teilnehmen. Wird ein Nutzer aus einer Phase ausgeschlossen kann er für
        diese Phase kein Angebot abgeben.
      </div>
    );
  }
  function injectPayload() {
    if (phasen && phasen.length > 0) {
      return phasen.map((p) => (
        <>
          <AlphaPhaseParticipationTable
            key={p.id}
            phase={p}
            serviceProvider={serviceProvider}
            participationState={keyValueStore}
            onParticipationChange={updateUserToPhase}
            onOpenEpCatalog={openEpCatalogView}
          />
        </>
      ));
    }
    return <NoPhases />;
  }

  function renderEpCatalogViewDialog() {
    if (!epCatalogViewPhase) {
      return null;
    }
    const keyValuesForPhaseAndUser =
      epCatalogViewUser && epCatalogViewPhase
        ? phaseUserKeyValues[
            buildPhaseUserCacheKey(epCatalogViewPhase.id, epCatalogViewUser.id)
          ] ?? null
        : null;
    const titleSuffix = epCatalogViewPhase.title ?? '';
    const userLabel = epCatalogViewUser?.username
      ? ' – ' + epCatalogViewUser.username
      : '';
    return (
      <AlphaPhaseEpCatalogViewDialog
        visible={epCatalogViewVisible}
        phaseId={epCatalogViewPhase.id}
        header={'EP-Katalog: ' + titleSuffix + userLabel}
        keyValues={keyValuesForPhaseAndUser}
        epCatalog={epCatalogTree}
        isLoading={epCatalogLoading || epCatalogSaving}
        onHide={closeEpCatalogView}
        onSave={epCatalogReadOnly ? undefined : handleEpCatalogSave}
        readOnly={epCatalogReadOnly}
      />
    );
  }

  return (
    <div className={'alpha-setup-phase-to-user m-2 mt-4 ml-1 p-2'}>
      {injectDescription()}
      {!dataIsLoading ? (
        injectPayload()
      ) : (
        <BeeLoadingSpinner strokeWidth={'3'} type={'secondary'} />
      )}
      <Toast ref={toast} position={'top-right'} />
      {epCatalogViewVisible && renderEpCatalogViewDialog()}
    </div>
  );
}
